import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Package, Building2, FileText, LayoutDashboard, LogOut, Menu, X, Headphones, ShieldCheck, Settings, ChevronDown, Check, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "./ui/dropdown-menu";
import Avatar from "./Avatar";
import { useState } from "react";
import { Button } from "./ui/button";
import { SUPORTE_URL } from "../pages/Login";

const navItems = [
  { path: "/", label: "Início", icon: LayoutDashboard },
  { path: "/encomendas", label: "Encomendas", icon: Package, perm: "ver_encomendas" },
  { path: "/unidades", label: "Unidades", icon: Building2, perm: "ver_unidades" },
  { path: "/relatorios", label: "Relatórios", icon: FileText, perm: "ver_relatorios" },
  { path: "/acessos", label: "Acessos", icon: ShieldCheck, perm: "gerenciar_acessos" },
];

const NOME_CARGO = { porteiro: "Colaborador", zelador: "Zelador", sindico: "Síndico" };

export default function Layout() {
  const { condominio, usuario, logout, pode, condominios, trocarCondominio } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [trocando, setTrocando] = useState(null);
  const varios = (condominios || []).length > 1;
  const trocar = async (id) => {
    if (id === condominio?.id) return;
    setTrocando(id);
    try { await trocarCondominio(id); } catch { setTrocando(null); }
  };
  const itens = navItems.filter((i) => !i.perm || pode(i.perm));

  const suporte = `${SUPORTE_URL}?text=${encodeURIComponent(
    `Olá! Preciso de suporte no Sistema de Encomendas.\nCondomínio: ${condominio?.nome || ""}\nUsuário: ${usuario?.usuario || usuario?.nome || ""}`
  )}`;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-slate-900 text-white shadow-lg sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src="/lobo-laranja.png" alt="" className="w-10 h-10 shrink-0 object-contain" />
              <div className="min-w-0">
                <h1 className="font-heading text-lg xl:text-xl tracking-tight leading-tight whitespace-nowrap">Sistema de Encomendas</h1>
                {varios ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1 text-xs text-orange-400 font-semibold hover:text-orange-300 max-w-[50vw] md:max-w-[200px] xl:max-w-xs" data-testid="trocar-condominio">
                        <span className="truncate">{condominio?.nome}</span>
                        {trocando ? <Loader2 className="w-3 h-3 animate-spin shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-72">
                      <DropdownMenuLabel className="text-xs text-slate-500 font-normal">Trocar de condomínio</DropdownMenuLabel>
                      {condominios.map((c) => (
                        <DropdownMenuItem key={c.id} onClick={() => trocar(c.id)} className={`py-2.5 ${c.id === condominio?.id ? "bg-orange-50 font-semibold" : ""}`} data-testid={`condo-${c.codigo}`}>
                          <span className="w-4 mr-1">{c.id === condominio?.id && <Check className="w-4 h-4 text-orange-500" />}</span>
                          <span className="flex-1 truncate">{c.nome}</span>
                          <span className={`ml-2 text-xs font-bold ${c.pendentes ? "text-orange-600" : "text-green-600"}`}>{c.pendentes}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <p className="text-xs text-slate-400 truncate max-w-[45vw] md:max-w-[160px] xl:max-w-xs">{condominio?.nome}</p>
                )}
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-0.5">
              {itens.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-2.5 xl:px-3.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive ? "bg-white/10 text-white" : "text-slate-300 hover:text-white hover:bg-white/5"
                    }`
                  }
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <a
                href={suporte}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-md text-sm font-medium transition-colors"
                data-testid="support-link"
              >
                <Headphones className="w-4 h-4" /> Suporte
              </a>
              <NavLink to="/conta" className="hidden lg:flex items-center gap-2 pl-1 pr-1 py-1 rounded-lg hover:bg-white/5" title="Minha conta" data-testid="topo-perfil">
                <Avatar nome={usuario?.nome} foto={usuario?.foto_url} tamanho={36} />
                <span className="hidden xl:block text-right leading-tight">
                  <span className="block text-sm text-white">{usuario?.nome?.split(" ")[0]}</span>
                  <span className="block text-[11px] text-slate-400">{NOME_CARGO[usuario?.cargo] || usuario?.cargo}</span>
                </span>
              </NavLink>
              <NavLink to="/conta" title="Minha conta" data-testid="btn-engrenagem"
                className={({ isActive }) => `hidden lg:flex p-2 rounded-md ${isActive ? "bg-orange-500/20 text-orange-400" : "text-slate-300 hover:text-white hover:bg-white/10"}`}>
                <Settings className="w-5 h-5" />
              </NavLink>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-300 hover:text-white hover:bg-white/10"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-white/10"
                data-testid="mobile-menu-button"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden py-3 border-t border-slate-700">
              {itens.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-md text-base font-medium ${
                      isActive ? "bg-white/10 text-white" : "text-slate-300 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
              <NavLink to="/conta" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 mt-1 rounded-md text-slate-300 hover:text-white hover:bg-white/5 text-base font-medium" data-testid="menu-conta">
                <Avatar nome={usuario?.nome} foto={usuario?.foto_url} tamanho={28} />
                <span className="flex-1">Minha conta</span>
                <Settings className="w-5 h-5" />
              </NavLink>
              <a
                href={suporte}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 mt-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-base font-medium"
                data-testid="support-link-mobile"
              >
                <Headphones className="w-5 h-5" /> Suporte no WhatsApp
              </a>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
